import { WaypointPosition } from "@/components/r3f/renderers/waypoint-label-render";
import { IElementPhotogrammetryScan } from "@/types/ielement-extra-types";
import {
  offsetPlaceholders,
  OffsetPlaceholdersReturn,
} from "@/utils/offset-placeholders";
import { useClipFrustum } from "@faro-lotv/app-component-toolbox";
import {
  IElement,
  IElementImg2d,
  IElementImg360,
  IElementRoomSection,
  isIElementImg2d,
  isIElementImg360,
  isIElementRoomSection,
} from "@faro-lotv/ielement-types";
import { useMemo } from "react";
import { Plane, Vector3 } from "three";

type UseVisiblePlaceholdersProps<T> = {
  /** All available placeholders */
  placeholders: T[];

  /** The positions of the placeholders */
  positions: Vector3[];

  /** The clipping planes to clip the placeholders by. If undefined, all placeholders are returned */
  clippingPlanes?: Plane[];
};

export type VisiblePlaceholders<T> = {
  /** The placeholders that are considered visible */
  visiblePlaceholders: T[];

  /** The positions of the visible placeholders */
  visiblePositions: Vector3[];
};

/**
 * @returns The visible placeholders and their positions clipped by clipping planes
 */
export function useVisiblePlaceholders<T>({
  placeholders,
  positions,
  clippingPlanes,
}: UseVisiblePlaceholdersProps<T>): VisiblePlaceholders<T> {
  const clipFrustum = useClipFrustum(clippingPlanes);

  return useMemo<VisiblePlaceholders<T>>(() => {
    // if positions is not ready yet, return an empty object
    if (placeholders.length !== positions.length) {
      return { visiblePlaceholders: [], visiblePositions: [] };
    }

    if (!clipFrustum) {
      // If no clipping planes are provided, then all placeholders are visible
      return {
        visiblePlaceholders: placeholders,
        visiblePositions: positions,
      };
    }

    // Only placeholders inside the clipping frustum are visible
    const visiblePlaceholders: T[] = [];
    const visiblePositions: Vector3[] = [];
    for (let i = 0; i < placeholders.length; i++) {
      const placeholder = placeholders[i];
      if (clipFrustum.containsPoint(positions[i])) {
        visiblePlaceholders.push(placeholder);
        visiblePositions.push(positions[i]);
      }
    }
    return { visiblePlaceholders, visiblePositions };
  }, [clipFrustum, placeholders, positions]);
}

/**
 * @returns the placeholder positions shifted using the offsetPlaceholders function
 * @param visiblePositions the positions to shift
 */
export function useOffsetPlaceholders(
  visiblePositions: Vector3[],
): OffsetPlaceholdersReturn {
  return useMemo(
    () => offsetPlaceholders(visiblePositions),
    [visiblePositions],
  );
}

export type IElementImg = IElementImg360 | IElementImg2d;

export type IElementWaypoint =
  | IElementImg
  | IElementRoomSection
  | IElementPhotogrammetryScan;

/**
 * @returns whether the element is a valid IElementWaypoint
 * @param element The element to check
 */
export function isIElementWaypoint(
  element: IElement,
): element is IElementWaypoint {
  return (
    isIElementImg360(element) ||
    isIElementImg2d(element) ||
    isIElementRoomSection(element)
  );
}

/**
 * @returns the placeholder elements and positions and their final render positions wrapped in a WaypointPosition object
 * @param visiblePlaceholders the visible placeholders
 * @param visiblePositions the positions of visible placeholders
 * @param panoYOffset optional offset to apply to all final render positions
 */
export function useWaypoints(
  visiblePlaceholders: IElementWaypoint[],
  visiblePositions: Vector3[],
  panoYOffset?: number,
): WaypointPosition[] {
  return useMemo(() => {
    if (visiblePlaceholders.length !== visiblePositions.length) {
      return new Array<WaypointPosition>();
    }
    return visiblePlaceholders.map((placeHolder, index) => {
      let renderPosition = visiblePositions[index];

      if (panoYOffset !== undefined) {
        renderPosition = renderPosition.clone();
        renderPosition.y += panoYOffset;
      }

      return { placeHolder, renderPosition };
    });
  }, [visiblePlaceholders, visiblePositions, panoYOffset]);
}
